const DEFAULT_INTERVAL_MS = 5000;

/**
 * The homepage hero photo carousel: up to five slides absolutely stacked in
 * one box, crossfaded on a timer (av-studio-hero.liquid renders the markup).
 * The timer pauses while the carousel is hovered or focused within and while
 * the tab is hidden, and never starts under prefers-reduced-motion. Dots and
 * pointer swipes remain available in every mode.
 */
class AvHeroCarousel extends HTMLElement {
  /** @type {number | undefined} */
  #timeoutId;

  /** @type {HTMLElement[]} */
  #slides = [];

  /** @type {HTMLButtonElement[]} */
  #dots = [];

  /** @type {number} */
  #index = 0;

  /** @type {boolean} */
  #hovering = false;

  /** @type {boolean} */
  #focusWithin = false;

  #manuallyPaused = false;

  #pointerStart = null;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  #listeners;

  /** @type {HTMLButtonElement | null} */
  #rotationControl = null;

  connectedCallback() {
    clearTimeout(this.#timeoutId);
    this.#slides = Array.from(this.querySelectorAll('.av-studio-hero__slide'));
    this.#dots = Array.from(this.querySelectorAll('.av-studio-hero__dot'));
    if (this.#slides.length < 2) return;

    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.#rotationControl = this.querySelector('.av-studio-hero__rotation');
    this.#rotationControl?.addEventListener('click', () => {
      this.#manuallyPaused = !this.#manuallyPaused;
      this.#schedule();
    }, options);

    this.#dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.#show(index);
        this.#schedule();
      }, options);
    });
    this.addEventListener('mouseenter', this.#handleHover, options);
    this.addEventListener('mouseleave', this.#handleHover, options);
    this.addEventListener('focusin', this.#handleFocus, options);
    this.addEventListener('focusout', this.#handleFocus, options);
    this.addEventListener('pointerdown', this.#handlePointerDown, options);
    this.addEventListener('pointerup', this.#handlePointerEnd, options);
    this.addEventListener('pointercancel', this.#handlePointerEnd, options);
    document.addEventListener('visibilitychange', this.#schedule, options);
    this.#reducedMotion.addEventListener('change', this.#schedule, options);

    this.#schedule();
  }

  disconnectedCallback() {
    clearTimeout(this.#timeoutId);
    this.#listeners?.abort();
    this.#pointerStart = null;
  }

  /**
   * (Re)starts the auto-advance countdown, or clears it while paused. Every
   * state change funnels through here, so pausing always cancels the pending
   * advance and resuming always waits a full interval again.
   */
  #schedule = () => {
    clearTimeout(this.#timeoutId);
    if (this.#rotationControl) {
      const label = this.#manuallyPaused ? this.#rotationControl.dataset.playLabel : this.#rotationControl.dataset.pauseLabel;
      this.#rotationControl.setAttribute('aria-label', label || '');
      this.#rotationControl.classList.toggle('av-studio-hero__rotation--paused', this.#manuallyPaused);
    }
    this.removeAttribute('data-playing');
    if (this.#manuallyPaused || this.#reducedMotion.matches || this.#hovering || this.#focusWithin || this.#pointerStart || document.visibilityState !== 'visible') {
      return;
    }

    // Restart the progress indicator with the new full interval.
    void this.offsetWidth;
    this.setAttribute('data-playing', '');
    this.#timeoutId = window.setTimeout(() => {
      this.#show((this.#index + 1) % this.#slides.length);
      this.#schedule();
    }, Number(this.dataset.interval) || DEFAULT_INTERVAL_MS);
  };

  /**
   * @param {MouseEvent} event
   */
  #handleHover = (event) => {
    if (!window.matchMedia('(hover: hover)').matches) return;
    this.#hovering = event.type === 'mouseenter';
    this.#schedule();
  };

  /**
   * @param {FocusEvent} event
   */
  #handleFocus = (event) => {
    // A tap can leave a dot focused indefinitely on mobile. Pause for keyboard focus.
    this.#focusWithin = event.type === 'focusin' && event.target.matches(':focus-visible');
    this.#schedule();
  };

  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' || event.target.closest('.av-studio-hero__dots')) return;
    this.#pointerStart = { x: event.clientX, y: event.clientY };
    this.#schedule();
  };

  #handlePointerEnd = (event) => {
    if (!this.#pointerStart) return;
    const dx = event.clientX - this.#pointerStart.x;
    const dy = event.clientY - this.#pointerStart.y;
    this.#pointerStart = null;
    if (event.type === 'pointerup' && Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.2) {
      this.#show((this.#index + (dx < 0 ? 1 : this.#slides.length - 1)) % this.#slides.length);
    }
    this.#schedule();
  };

  /**
   * Crossfades to the slide at index and mirrors the state onto the dots.
   *
   * @param {number} index
   */
  #show(index) {
    this.#index = index;
    this.#slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('av-studio-hero__slide--active', slideIndex === index);
      slide.setAttribute('aria-hidden', String(slideIndex !== index));
    });
    this.#dots.forEach((dot, dotIndex) => {
      if (dotIndex === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('av-hero-carousel')) {
  customElements.define('av-hero-carousel', AvHeroCarousel);
}
